import type { AppError, DetailsOf } from '@core/api-clients/types.js';

/**
 * Single source of truth for the SPA's user-facing strings.
 *
 * Convention (see `.claude/rules/global/ui-text.md`):
 *  - Keys in **English** so a non-Dutch reader can navigate the codebase by key.
 *  - Values in **Dutch** — the only locale shipped in v2.
 *  - Functions on `t` are template patterns; call sites pass parameters.
 *  - Components import `t` directly and reference values by key — never inline
 *    Dutch literals in JSX, attributes, Zod schema messages, or anywhere else.
 *
 * The shape is forward-compatible with introducing a runtime locale later
 * (each leaf is a string or a pure function returning a string), but adding
 * a locale toggle is explicitly *not* on the v2 roadmap — don't preemptively
 * wrap this in an `i18next`-style hook or context provider.
 */
export const t = {
  app: {
    loading: 'Laden…',
    reauthing: 'Werkruimte laden…',
    sessionExpiredRedirect: 'Sessie verlopen — bezig met opnieuw aanmelden…',
    sessionInvalidated: {
      title: 'Je sessie is niet meer geldig',
      description:
        'Meld je opnieuw aan om verder te gaan. Je eerder opgeslagen werk blijft behouden.',
      signOut: 'Uitloggen en opnieuw aanmelden',
    },
    bootstrapError: {
      title: 'Je profiel kon niet worden geladen',
      description:
        'We konden je gebruikersgegevens niet ophalen. Probeer het opnieuw, of meld je opnieuw aan als het probleem aanhoudt.',
      reload: 'Opnieuw proberen',
      signOut: 'Uitloggen',
    },
    sidebar: {
      mainAriaLabel: 'Hoofdnavigatie',
      activeProjectAriaLabel: 'Navigatie binnen actief project',
      recentProjectsAriaLabel: 'Recente projecten',
      projectLabel: 'Project',
      collapse: 'Menu inklappen',
      expand: 'Menu uitklappen',
      toggle: 'Menu in- of uitklappen',
      openMobile: 'Menu openen',
      homeLink: 'Naar de startpagina',
      mobileTitle: 'Navigatie',
      mobileDescription: 'Hoofdnavigatie van de applicatie',
    },
    close: 'Sluiten',
    error: {
      title: 'Er ging iets mis',
      description:
        'Er is iets misgegaan. Opgeslagen werk blijft behouden — probeer de pagina opnieuw te laden.',
      reload: 'Opnieuw laden',
    },
    pageError: {
      title: 'Er ging iets mis',
      description: 'We konden deze pagina niet laden. Probeer het opnieuw.',
      retry: 'Opnieuw proberen',
    },
    newVersion: {
      title: 'Nieuwe versie beschikbaar',
      description:
        'Er is een nieuwe versie van NOMA Platform. Vernieuw de pagina om verder te gaan. Opgeslagen werk blijft behouden.',
      reload: 'Pagina vernieuwen',
      toastTitle: 'Nieuwe versie beschikbaar',
      toastDescription:
        'Vernieuw de pagina om de nieuwste versie te gebruiken. Opgeslagen werk blijft behouden.',
    },
  },

  auth: {
    signInRedirect: {
      message: 'Bezig met doorsturen naar de inlogpagina…',
    },
    signedOut: {
      title: 'Je bent uitgelogd',
      description: 'Log opnieuw in om verder te gaan.',
      buttonIdle: 'Inloggen',
      buttonLoading: 'Laden…',
      websiteLink: 'Naar noma.eco',
    },
    callback: {
      signingIn: 'Bezig met inloggen…',
      error: {
        title: 'Inloggen mislukt',
        description: 'Er ging iets mis bij het inloggen. Probeer het opnieuw.',
        retry: 'Opnieuw proberen',
      },
      cancelled: {
        title: 'Inloggen afgebroken',
        description: 'Het inloggen is niet voltooid. Probeer het opnieuw.',
      },
    },
    requireAuth: {
      loading: 'Laden…',
    },
    signOut: 'Uitloggen',
    devSignIn: {
      failed: 'Ontwikkel-inloggen mislukt. Probeer het opnieuw.',
    },
    devLogin: {
      title: 'Ontwikkelmodus — testgebruikers',
      description: 'Log in als een testgebruiker zonder de WorkOS-inlogpagina.',
      empty: 'Geen testgebruikers geconfigureerd.',
      menuItem: 'Dev-login',
    },
  },

  debugBlock: {
    code: 'Foutcode',
    reference: 'Referentie',
    copy: 'Kopieer',
    copied: 'Gekopieerd',
  },

  // Code-keyed translations for `AppError.code` values surfaced by ErrorPanel.
  // Each entry is either a string (no details consumed) or a function
  // (details => string) when interpolating per-occurrence data. Codes not
  // listed fall back to the backend's English `message` at render time —
  // that's by design, see docs/features/error-handling-policy/design.md.
  //
  // `satisfies` clause type-checks every function-form entry against
  // DetailsOf<C> so a leaf-class detail-shape change at the backend forces
  // a matching update here.
  errors: {
    NOT_PROJECT_MEMBER: 'Je hebt geen toegang tot dit project',
    NOT_TEAM_MEMBER: 'Je hebt geen toegang tot deze werkruimte',
    PROJECT_NOT_FOUND: 'Dit project bestaat niet of is verwijderd',
    REPORT_NOT_FOUND: 'Dit rapport bestaat niet of is verwijderd',
    ATTACHMENT_NOT_FOUND: 'Deze bijlage bestaat niet of is verwijderd',
    PROJECT_MODIFIED_BY_OTHER:
      'Iemand anders heeft dit project gewijzigd. Vernieuw de pagina.',
    REPORT_MODIFIED_BY_OTHER:
      'Iemand anders heeft dit rapport gewijzigd. Vernieuw de pagina.',
    TOKEN_INVALID: 'Je sessie is verlopen. Log opnieuw in.',
    TOKEN_MISSING: 'Je sessie is verlopen. Log opnieuw in.',
    TOKEN_MISSING_CLAIMS: 'Je sessie is verlopen. Log opnieuw in.',
    TEAM_NOTIFICATION_FAILED:
      'Ons team kon niet op de hoogte worden gebracht, dus de actie is niet voltooid. Probeer het opnieuw of neem contact op via info@noma.eco.',
    TRIAL_ALREADY_ACTIVE: 'Er is al een actieve Pro-proefperiode.',
    TRIAL_ALREADY_USED:
      'Deze proefperiode is al gebruikt. Proefperiode verlengen? Neem contact op voor de mogelijkheden.',
    TRIAL_NOT_PAUSED:
      'Er is geen geannuleerde proefperiode met resterende dagen. Proefperiode verlengen? Neem contact op voor de mogelijkheden.',
    TRIAL_NOT_ACTIVE:
      'Er loopt geen proefperiode om te annuleren. Vernieuw de pagina om de actuele status te zien.',
    PLAN_ALREADY_PRO:
      'Pro is al actief voor deze organisatie. Een proefperiode is niet nodig.',
    PLAN_PERIOD_IN_FORCE:
      'Voor deze organisatie loopt al een ander abonnement. Zolang dat loopt, is een proefperiode niet mogelijk. Neem contact op voor de mogelijkheden.',
    AGENT_VAULT_INVALID:
      'De verbinding met de schrijfhulp werkt niet meer. Verbind opnieuw om verder te gaan.',
    INTERNAL_ERROR: 'Er is een interne fout opgetreden. Probeer het later opnieuw.',
    INSUFFICIENT_ROLE: (details: DetailsOf<'INSUFFICIENT_ROLE'>) =>
      `Voor deze actie heb je de rol "${details.requiredRole}" nodig.`,
    VALIDATION_ERROR: 'De ingevoerde gegevens zijn niet geldig.',
    REPORT_DATA_VALIDATION_FAILED:
      'De ingevulde gegevens voldoen niet aan de validatieregels.',
    PROJECT_LIMIT_REACHED:
      'De projectlimiet van dit abonnement is bereikt. Upgrade naar Pro voor onbeperkt projecten.',
    PROJECT_READ_ONLY:
      'Dit project is alleen-lezen op het huidige abonnement. Upgrade naar Pro om het te bewerken.',
  } satisfies Partial<{
    [C in AppError['code']]: string | ((details: DetailsOf<C>) => string);
  }>,

  nav: {
    allProjects: 'Alle projecten',
    overview: 'Overzicht',
    newProject: 'Nieuw project',
    personalScope: 'Persoonlijk',
    recentProjects: {
      label: 'Recente projecten',
      empty: 'Je recent geopende projecten verschijnen hier.',
    },
    toolSidebarAriaLabel: 'Tools voor dit project',
    toolsSectionLabel: 'Tools',
  },
  releaseToggles: {
    menuItem: 'Release-toggles',
    title: 'Release-toggles',
    description:
      'Functies in ontwikkeling zijn hier per organisatie in of uit te schakelen.',
    empty: 'Functies in ontwikkeling verschijnen hier zodra ze beschikbaar zijn.',
    loadFailed: 'De toggles konden niet geladen worden. Probeer het opnieuw.',
    retry: 'Opnieuw proberen',
    // The toggle key is a machine constant (English) — only the surrounding
    // action copy is Dutch.
    switchAriaLabel: ({ key }: { key: string }) => `${key} in- of uitschakelen`,
  },

  account: {
    menuItem: 'Accountinstellingen',
    title: 'Accountinstellingen',
    tabs: {
      profile: 'Profiel',
      security: 'Beveiliging',
      subscription: 'Abonnement',
      terms: 'Voorwaarden & privacy',
    },
    profile: {
      heading: 'Profiel',
      description: 'Naam en e-mailadres worden hier beheerd.',
    },
    security: {
      heading: 'Wachtwoord',
      description: 'Wachtwoord instellen of wijzigen.',
      sessionsHeading: 'Actieve sessies',
      sessionsDescription:
        'Apparaten en browsers die op dit account zijn ingelogd — onbekende sessies zijn hier af te melden.',
    },
    subscription: {
      currentPlanLabel: 'Je huidige abonnement:',
      freeTierLabel: 'Gratis',
      proTierLabel: 'Pro',
      proTrialLabel: 'Pro (proefperiode)',
      free: {
        description:
          'Voor wie eerst wil proberen. Bevat de drie checkers en plangebied-tekenen.',
        currentCta: 'Huidig abonnement',
      },
      pro: {
        currentCta: 'Huidig abonnement',
        priceSuffix: 'per gebruiker / maand',
        priceNote: 'Excl. btw, maandelijks opzegbaar.',
        description: 'Onbeperkt projecten, alle tools en GIS-data import/export.',
        startTrial: 'Gratis proefperiode starten',
        trialNote: '30 dagen proberen, geen creditcard nodig',
        trialActiveCta: 'Proefperiode actief',
        trialActiveNote: ({ date }: { date: string }) => `Actief tot ${date}`,
        resumeTrial: 'Proefperiode heractiveren',
        remainingNote: ({ days }: { days: number }) =>
          `Nog ${days} ${days === 1 ? 'dag' : 'dagen'} proefperiode over`,
      },
      trialPanel: {
        badge: 'PROEFPERIODE',
        body: ({ date }: { date: string }) =>
          `Proefperiode loopt tot ${date}. Daarna keert het account terug naar Gratis, tenzij er een abonnement wordt afgesloten.`,
        subscribe: 'Abonnement afsluiten',
        requestReceived: 'Aanvraag ontvangen',
        pause: 'Proefperiode annuleren',
      },
      subscriptionDialog: {
        title: 'Abonnement afsluiten',
        description:
          'We helpen je graag persoonlijk met het opzetten van je Pro-abonnement. Ons team neemt binnen één werkdag contact op via je e-mailadres om de afronding samen door te lopen.',
        messageLabel: 'Bericht (optioneel)',
        messagePlaceholder: 'Bijvoorbeeld het aantal gebruikers of facturatiewensen',
        cancel: 'Annuleren',
        submitIdle: 'Verzoek versturen',
        submitPending: 'Bezig met versturen…',
        successTitle: 'Verzoek verstuurd',
        successBody: 'Ons team neemt binnen één werkdag contact op via je e-mailadres.',
        successClose: 'Sluiten',
      },
      trialDialog: {
        title: 'Start je gratis proefperiode',
        description:
          'De proefperiode geeft 30 dagen toegang tot alles van Pro. Daarna keert het account terug naar Gratis, tenzij er een abonnement wordt afgesloten. Geen creditcard nodig.',
        endDateLabel: 'Proefperiode actief tot',
        optionalToggle: 'Vertel ons meer over jezelf (optioneel)',
        organisationLabel: 'Organisatie',
        organisationPlaceholder: 'Bureau Natuurlijk',
        roleLabel: 'Functie',
        rolePlaceholder: 'Ecoloog, projectleider',
        useCaseLabel: 'Waar ga je het voor gebruiken?',
        useCasePlaceholder: 'Quickscans voor woningbouwprojecten in Gelderland',
        cancel: 'Annuleren',
        submitIdle: 'Proefperiode starten',
        submitPending: 'Bezig met starten…',
        successTitle: 'Je proefperiode is gestart',
        successBody: ({ date }: { date: string }) =>
          `Pro is nu actief tot ${date}. Veel plezier met alle tools en onbeperkt projecten.`,
        successClose: 'Aan de slag',
      },
      matrix: {
        columns: { feature: 'Wat je krijgt', free: 'Gratis', pro: 'Pro' },
        soon: 'Binnenkort',
        included: 'Inbegrepen',
        sections: {
          collaboration: 'Projecten en samenwerking',
          tools: 'Toegang tot tools',
          export: 'Export',
        },
        rows: {
          projectsCount: 'Aantal projecten',
          teamCollaboration: 'Samenwerken met teamleden',
          basicTools: 'Basistools (Plangebied, N2000, NNN)',
          otherTools: 'Overige tools',
          otherToolsLink: 'bekijk hier',
          earlyAccess: 'Vroege toegang tot nieuwe tools',
          imageExport: 'Afbeelding-export',
          gisExport: 'GIS-data import en export',
        },
        unlimited: 'Onbeperkt',
      },
    },
    terms: {
      documentsHeading: 'Voorwaarden & privacy',
      acceptance:
        'De Voorwaarden en het Privacybeleid van NOMA zijn terug te lezen op onze website.',
      readTerms: 'Voorwaarden lezen',
      readPrivacy: 'Privacybeleid lezen',
      cookiesHeading: 'Cookies',
      cookiesDescription:
        'Beheer welke cookies actief zijn of bekijk de cookieverklaring.',
    },
  },

  projects: {
    list: {
      heading: 'Projecten',
      loading: 'Projecten laden…',
      empty: 'Geen projecten. Maak er een aan om te beginnen.',
      search: {
        placeholder: 'Zoek op naam…',
        empty: 'Geen projecten gevonden voor deze zoekterm.',
      },
      groups: {
        recent: 'Recent',
        thisWeek: 'Deze week',
        older: 'Eerder',
      },
      sort: {
        label: 'Sorteren',
        recentlyVisited: 'Laatst bezocht',
        recentlyUpdated: 'Laatst gewijzigd',
        newest: 'Nieuwste eerst',
        oldest: 'Oudste eerst',
      },
      loadMore: 'Meer laden',
      loadMoreError:
        'De volgende projecten konden niet geladen worden. Probeer het opnieuw.',
    },
    create: {
      cta: 'Nieuw project',
      title: 'Nieuw project',
      description: 'De naam kan later nog aangepast worden.',
      nameLabel: 'Naam',
      namePlaceholder: 'Aanleg fietspad centrum Utrecht',
      submitIdle: 'Project aanmaken',
      submitPending: 'Aanmaken…',
      cancel: 'Annuleren',
      errors: {
        nameRequired: 'Naam is verplicht',
        nameTooLong: 'Naam is te lang',
      },
    },
    limit: {
      banner: ({ usage, limit }: { usage: number; limit: number }) =>
        `Je gebruikt ${usage} van ${limit} projecten.`,
      includesDeleted: 'Dit is inclusief verwijderde projecten.',
      bannerUpgradeHint: ({ plan }: { plan: string }) =>
        `Upgrade naar ${plan} voor onbeperkt projecten.`,
      upgrade: 'Upgrade',
      reachedTitle: 'Limiet bereikt',
      reachedDescription: ({
        usage,
        limit,
        plan,
      }: {
        usage: number;
        limit: number;
        plan: string;
      }) =>
        `Je hebt ${usage} van ${limit} projecten gebruikt. Dit is inclusief verwijderde projecten. Upgrade naar ${plan} voor onbeperkt projecten, alle tools en GIS-data import/export.`,
      upgradeToPlan: ({ plan }: { plan: string }) => `Upgrade naar ${plan}`,
      cancel: 'Annuleren',
      readOnly: ({ plan }: { plan: string }) =>
        `Dit project is alleen-lezen. Upgrade naar ${plan} om het te bewerken.`,
    },
    detail: {
      loading: 'Laden…',
      header: {
        nameRequired: 'Naam mag niet leeg zijn.',
        menu: {
          trigger: 'Meer acties',
          delete: 'Project verwijderen',
        },
        deleteDialog: {
          title: 'Project verwijderen',
          description: ({ name }: { name: string }) =>
            `Het project "${name}" wordt verwijderd, inclusief alle rapportgegevens en bijlagen. Dit kan niet ongedaan worden gemaakt.`,
          confirmLabel: 'Typ de projectnaam ter bevestiging',
          confirm: 'Project verwijderen',
          pending: 'Verwijderen…',
          cancel: 'Annuleren',
        },
      },
      sections: {
        projectArea: 'Plangebied',
        quickscan: 'Quickscan',
        furtherResearch: 'Nader onderzoek',
        activityPlan: 'Activiteitenplan',
      },
    },
  },

  sections: {
    projectArea: {
      prerequisite: {
        missing: {
          title: 'Plangebied ontbreekt',
          description: 'Definieer eerst een plangebied om deze tool te gebruiken.',
          cta: 'Naar Plangebied',
        },
        incomplete: {
          title: 'Plangebied niet afgerond',
          description:
            'Het plangebied is nog niet volledig ingetekend. Rond het af om deze tool te gebruiken.',
          cta: 'Plangebied afronden',
        },
      },
      summary: {
        empty: 'Nog geen plangebied ingesteld.',
      },
    },
    n2000: {
      summary: {
        empty: 'N2000 analyse nog niet uitgevoerd.',
      },
    },
    provincial: {
      summary: {
        empty: 'NNN analyse nog niet uitgevoerd.',
      },
    },
    pondBat: {
      summary: {
        empty: 'Meervleermuis-analyse nog niet uitgevoerd.',
      },
    },
    species: {
      summary: {
        empty: 'Soortenanalyse nog niet uitgevoerd.',
      },
    },
  },

  conflict: {
    projectChangedElsewhere: 'Dit project is elders gewijzigd.',
    reportChangedElsewhere: 'Dit rapport is elders gewijzigd.',
    body: 'Vernieuw de pagina om verder te werken — je niet-opgeslagen wijzigingen in dit onderdeel gaan verloren.',
    reload: 'Vernieuwen',
  },

  /**
   * 2-way dialog shown when leaving a tool screen (in-SPA navigation or the
   * done action) while the editor holds uncommitted in-progress edits. Stay
   * keeps the edit; leaving drops it.
   */
  leaveWarning: {
    title: 'Niet-opgeslagen wijzigingen',
    body: 'De niet-opgeslagen wijzigingen in dit onderdeel gaan verloren bij het verlaten van dit scherm.',
    stay: 'Doorgaan met bewerken',
    leave: 'Toch verlaten',
  },

  stale: {
    heading: 'Projectgebied gewijzigd na deze analyse',
    body: 'Deze analyse is uitgevoerd op een ouder projectgebied. Ververs de analyse om opnieuw te toetsen tegen het huidige gebied. Bestaande schermafbeeldingen blijven behouden.',
    refresh: 'Ververs analyse',
    // Upsell variant: re-running for a changed location needs a higher plan.
    upsellBody: ({ plan, targetPlan }: { plan: string; targetPlan: string }) =>
      `Deze analyse is uitgevoerd op een oudere versie van het projectgebied. Verversen is niet mogelijk binnen het huidige ${plan}-abonnement. Upgrade naar een ${targetPlan}-abonnement om de analyse te kunnen verversen.`,
    // Read-only variant (no refresh action): informs without offering to rebuild.
    readOnlyBody: 'Deze analyse is uitgevoerd op een ouder projectgebied.',
  },

  tools: {
    projectArea: 'Plangebied',
    intakeTool: 'Intake-tool',
    n2000: 'N2000-checker',
    provincial: 'NNN-checker',
    pondBatChecker: 'Meervleermuis-checker',
    speciesChecker: 'Soortenchecker',
    woodStandsChecker: 'Houtopstanden-checker',
    habitatTypeExplorer: 'Habitattypenverkenner',
    aeriusNitrogenModule: 'AERIUS-stikstofmodule',
    researchPlanner: 'Onderzoeksplanner',
    protocolChecker: 'Protocol-checker',
    fieldSurveyTool: 'Veldonderzoek-tool',
    cumulativeEffects: 'Cumulatieve effecten',
    /** Status pills on not-yet-available tools (sidebar + overview cards). */
    statusPill: {
      inDevelopment: 'In ontwikkeling',
      planned: 'Op de planning',
    },
    /** Overview tool-card actions: start an unstarted tool, edit a started one. */
    start: 'Starten',
    edit: 'Bewerken',
    /** Badge on an overview tool card whose analysis is stale vs the project area. */
    staleBadge: 'Verouderd',
    /**
     * Per-tool filename basis for map-screenshot exports:
     * `<slug(projectName)>-<value>.<ext>`. Stored as the attachment's
     * `original_filename` at capture time — edits here affect newly captured
     * screenshots only, never already-stored ones.
     */
    mapExportSlug: {
      projectArea: 'plangebied',
      n2000: 'n2000',
      provincial: 'nnn',
      pondBat: 'meervleermuis',
      species: 'soorten',
    },
    /** In-tool upsell shown when the org lacks the tool's required entitlement. */
    gate: {
      title: ({ plan }: { plan: string }) => `Beschikbaar met ${plan}`,
      body: ({ tool }: { tool: string }) =>
        `${tool} valt buiten het huidige abonnement. Start een gratis proefperiode om de tool meteen te gebruiken.`,
      viewPlans: 'Bekijk abonnementen',
      readOnlyTitle: 'Alleen-lezen',
      readOnlyBody: 'Wijzigingen aan deze tool worden niet opgeslagen.',
    },
  },

  toolScreen: {
    done: 'Gereed',
    saving: 'Opslaan…',
    saved: 'Opgeslagen',
    /**
     * Header helper beside the done button when the editor holds uncommitted
     * in-progress edits (drawing/modifying geometry not yet committed). Distinct
     * from the autosave states: nothing is queued to save yet.
     */
    dirty: 'Wijzigingen niet opgeslagen',
    /**
     * Save-status header treatment when an autosave fails — a genuine 409
     * conflict that could not be recovered, or any non-409 save error. Distinct
     * from `dirty` (uncommitted edits): here a save was attempted and failed.
     */
    saveFailed: 'Opslaan mislukt',
    saveFailedHint:
      'De laatste wijziging is niet opgeslagen. Zie de melding om verder te gaan.',
    /** Manual retry beside the save-status indicator after a non-409 failure. */
    saveRetry: 'Opnieuw proberen',
    /** Save-status header treatment on a read-only project (no autosave). */
    readOnly: 'Alleen-lezen',
    readOnlyHint: 'Wijzigingen worden niet opgeslagen',
    referenceEdit: 'Bewerken',
    referenceSummary: ({ count, strategy }: { count: number; strategy: string | null }) =>
      `${count} ${count === 1 ? 'deelgebied' : 'deelgebieden'}${strategy ? ` · ${strategy}` : ''}`,
  },

  switcher: {
    placeholderLabel: 'Alle projecten',
    searchPlaceholder: 'Zoek project…',
    sectionRecent: 'Recent',
    sectionAll: 'Alle projecten',
    activePill: 'Actief',
    newProject: 'Nieuw project',
    allProjectsAction: 'Alle projecten',
    emptyAll: 'Geen projecten gevonden.',
    emptyForQuery: ({ query }: { query: string }) =>
      `Geen projecten gevonden voor "${query}".`,
    ariaLabel: 'Projectkiezer',
  },

  consent: {
    banner: {
      title: 'Cookies op deze site',
      description:
        'Wij gebruiken cookies voor de werking van de applicatie, voor statistieken en voor onze marketing.',
      acceptAll: 'Alles accepteren',
      rejectAll: 'Alles weigeren',
      customize: 'Voorkeuren',
    },
    dialog: {
      title: 'Cookievoorkeuren',
      description:
        'Kies welke cookiecategorieën zijn toegestaan. Deze keuze is op elk moment aan te passen.',
      save: 'Voorkeuren opslaan',
      cancel: 'Annuleren',
      statementLinkLabel: 'Cookieverklaring',
    },
    categories: {
      necessary: {
        title: 'Strikt noodzakelijk',
        description:
          'Vereist voor inloggen en het actief houden van de sessie. Deze cookies zijn niet uit te schakelen.',
      },
      measurement: {
        title: 'Statistieken',
        description:
          'Product- en sessiestatistieken om de applicatie te verbeteren. Pas actief na toestemming.',
      },
      marketing: {
        title: 'Marketing',
        description:
          'Marketingcookies voor eigen campagnes. Wij meten het bereik van onze campagnes. Pas actief na toestemming.',
      },
    },
    controls: {
      openPreferences: 'Cookie-instellingen',
      openStatement: 'Cookieverklaring',
    },
    statement: {
      pageTitle: 'Cookieverklaring',
      back: 'Terug naar NOMA Platform',
      intro:
        'Hieronder staan de cookies en lokale opslag die wij gebruiken, per categorie. De voorkeur is aan te passen via de knop "Cookie-instellingen" onderaan de pagina.',
      table: {
        name: 'Naam',
        storage: 'Opslag',
        provider: 'Leverancier',
        purpose: 'Doel',
        retention: 'Bewaartermijn',
      },
      emptyCategory: 'Geen actieve cookies in deze categorie.',
      storage: {
        cookie: 'Cookie',
        localStorage: 'Lokale opslag',
        sessionStorage: 'Sessieopslag',
      },
      retention: {
        session: 'Sessie',
      },
      cookies: {
        workosSession: {
          purpose: 'Houdt de inlogsessie actief.',
        },
        c15tConsent: {
          purpose: 'Slaat de cookievoorkeuren op.',
        },
        phPosthog: {
          purpose:
            'Bewaart de PostHog-identifier en sessiestatus voor product- en sessiestatistieken.',
        },
        phWindowId: {
          purpose:
            'Onderscheidt browservensters binnen dezelfde sessie voor gebruiksanalyse.',
        },
      },
    },
  },
  memoryViewer: {
    tabLabel: 'Projectdossier',
    empty:
      'Er is nog geen projectdossier voor dit project. Start een sessie met de rapport-assistent om bronnen en bouwstenen vast te leggen.',
    back: 'Terug naar project',
    loadError: 'Het projectdossier kan niet worden geladen. Probeer het opnieuw.',
    retry: 'Opnieuw laden',
    views: {
      perSection: 'Per sectie',
      perSource: 'Per bron',
    },
    sections: {
      title: 'Rapportsecties',
      meta: ({ count, themes }: { count: number; themes: string }) =>
        `${count} bouwsteen${count === 1 ? '' : 'nen'} · thema's: ${themes}`,
      externalAllowed: 'externe bronnen toegestaan',
    },
    questions: {
      title: 'Vragen',
      hint: 'nog te beantwoorden of ter controle',
      openMeta: ({ date }: { date: string }) => `open · gesteld op ${date}`,
      parkedMeta: 'Komt aan bod bij het schrijven van deze sectie',
      answeredBy: 'beantwoord door',
      answeredGroup: ({ count }: { count: number }) => `Beantwoorde vragen (${count})`,
    },
    entities: {
      title: 'Entiteiten',
      emptyForSection: 'Geen entiteiten voor deze sectie.',
      declaredWithoutFacts: 'verwacht, nog geen informatie',
      factCount: ({ count }: { count: number }) =>
        `${count} bouwsteen${count === 1 ? '' : 'nen'}`,
    },
    facts: {
      emptyForSection: 'Nog geen bouwstenen.',
      allFacts: ({ count }: { count: number }) => `Alle bouwstenen (${count})`,
      possiblyRelated: ({ count }: { count: number }) =>
        `Mogelijk ook gerelateerd (${count})`,
      supersededGroup: ({ count }: { count: number }) =>
        `Vervallen bouwstenen (${count})`,
      superseded: 'vervallen',
      replacedBy: 'vervangen door',
      staleGrounds: 'verouderde onderbouwing',
      external: 'extern',
      typeMatch: 'mogelijk gerelateerd',
      expired: 'verlopen',
      about: 'Gaat over',
      refersTo: 'Verwijst naar',
      groundedBy: 'Onderbouwd door',
      grounds: 'Onderbouwt',
      sourcePrefix: 'bron:',
      confirmedBy: 'bevestigd door',
      expand: 'Details tonen',
    },
    entitySheet: {
      about: ({ title }: { title: string }) => `Over ${title}`,
      mentions: ({ title }: { title: string }) => `Bouwstenen die ${title} noemen`,
      readMore: 'lees meer',
      readLess: 'toon minder',
      noFacts: 'Geen bouwstenen.',
      noMentions: 'Geen verwijzingen.',
    },
    episodes: {
      title: 'Bronnen',
      selectPrompt: 'Selecteer een bron.',
      derivedCount: ({ count }: { count: number }) =>
        `${count} bouwsteen${count === 1 ? '' : 'nen'}`,
      sourceKind: 'Soort bron',
      coverage: 'dekking',
      received: 'Ontvangen',
      referenceTime: 'Referentietijd',
      bodyTitle: 'Brontekst',
      derivedTitle: ({ count }: { count: number }) => `Hieruit vastgelegd (${count})`,
      confirmsTitle: ({ count }: { count: number }) =>
        `Bevestigt eerdere informatie (${count})`,
    },
  },
  reportWriter: {
    /** Tab label — action-framed by design (docs/features/agent-sessions/design.md § SPA). */
    tabLabel: 'Rapport schrijven',
    openDossier: 'Projectdossier openen',
    draft: {
      title: 'Conceptrapport',
      empty:
        'Start het gesprek om aan het rapport te beginnen; het concept verschijnt hier na elke stap.',
      imageUnavailable:
        'Afbeelding niet beschikbaar. Vraag de assistent om de afbeelding opnieuw te uploaden.',
    },
    chat: {
      inputLabel: 'Bericht aan de assistent',
      placeholder: 'Schrijf het beschermde soorten hoofdstuk',
      send: 'Versturen',
      stop: 'Onderbreken',
      agentWorking: 'De assistent werkt aan het rapport…',
      pausedForReauth:
        'De verbinding met het platform is verlopen. Verbind opnieuw om verder te schrijven.',
      reauthCta: 'Opnieuw verbinden',
      reconnect: 'Opnieuw verbinden',
      streamError:
        'De verbinding met de assistent is verbroken. Vernieuw de pagina om verder te gaan.',
      streamErrorUnavailable:
        'De schrijfhulp is momenteel niet beschikbaar. Probeer het later opnieuw.',
      restartCta: 'Nieuwe sessie starten',
      /** Survivable upstream failures, rendered as transcript rows; keyed by the wire code. */
      transcriptError: {
        mcp_unavailable:
          'De verbinding tussen de assistent en NOMA werkt niet op dit moment. De assistent kan momenteel geen projectgegevens ophalen en opslaan. Probeer het nog eens; blijft dit misgaan, start dan een nieuwe sessie.',
      },
      scrollToEnd: 'Naar het einde',
    },
    steps: {
      /** Collapsed-turn summary: "3 stappen · 42 s". */
      summary: ({ count, seconds }: { count: number; seconds: number | null }) =>
        `${count} ${count === 1 ? 'stap' : 'stappen'}${seconds === null ? '' : ` · ${seconds} s`}`,
      liveHeader: ({ count }: { count: number }) => `Bezig · stap ${count}`,
      liveHeaderStarting: 'Bezig…',
      thinking: 'Aan het nadenken…',
      truncatedNote: 'Ingekort weergegeven',
      labels: {
        bash: 'Opdracht uitgevoerd',
        read: ({ file }: { file: string }) => `Bestand gelezen: ${file}`,
        write: ({ file }: { file: string }) => `Bestand geschreven: ${file}`,
        edit: 'Rapporttekst bewerkt',
        /** Memory-tool steps of the v2 agent, keyed by MCP tool name. */
        qs2: {
          qs2_create_project: 'Project aangemaakt',
          qs2_list_projects: 'Projecten opgevraagd',
          qs2_status: 'Dossierstatus gecontroleerd',
          qs2_digest: 'Dossieroverzicht opgevraagd',
          qs2_episode_add: 'Bron vastgelegd',
          qs2_episode_amend: 'Bron aangevuld',
          qs2_episode_get: 'Bron opgevraagd',
          qs2_entity_add_many: 'Entiteiten vastgelegd',
          qs2_entity_list: 'Entiteiten opgevraagd',
          qs2_entity_prose_set: 'Entiteitbeschrijving bijgewerkt',
          qs2_fact_add: 'Bouwstenen vastgelegd',
          qs2_fact_add_many: 'Reeks bouwstenen vastgelegd',
          qs2_fact_amend: 'Bouwsteen gecorrigeerd',
          qs2_fact_list: 'Bouwstenen opgevraagd',
          qs2_fact_trace: 'Herkomst van een bouwsteen opgevraagd',
          qs2_graph_neighbors: 'Samenhangende entiteiten opgevraagd',
          qs2_question_add: 'Open vraag vastgelegd',
          qs2_question_list: 'Open vragen opgevraagd',
          qs2_question_set: 'Open vraag bijgewerkt',
          qs2_question_reopen: 'Vraag heropend',
          qs2_section_context: 'Context voor sectie opgevraagd',
          qs2_section_note_set: 'Sectienotitie bijgewerkt',
          qs2_attachment_list: 'Bijlagen opgevraagd',
          qs2_attachment_upload_urls: 'Bijlage-upload voorbereid',
          qs2_attachment_download_urls: 'Bijlagen opgehaald',
          qs2_project_data_get: 'Projectgegevens opgevraagd',
          qs2_report_data_get: 'Rapportgegevens opgevraagd',
        },
      },
    },
    connect: {
      title: 'Schrijfhulp verbinden',
      body: 'Verbind de agent om de schrijfhulp te starten.',
      cta: 'Verbinden en starten',
      failed: 'Verbinden is niet gelukt. Probeer het opnieuw.',
      popupBlocked:
        'De browser blokkeerde het inlogvenster. Sta pop-ups toe en probeer opnieuw.',
      invalidated:
        'De verbinding met de schrijfhulp werkt niet meer. Verbind opnieuw om verder te gaan.',
      stale:
        'De verbinding met de schrijfhulp is verlopen. Verbind opnieuw om verder te gaan.',
    },
    start: 'Schrijfhulp starten',
    activeSession: {
      byOther: ({ name }: { name: string }) => `${name} werkt in dit rapport.`,
      byOtherUnknown: 'Een teamlid werkt in dit rapport.',
      lastActive: ({ when }: { when: string }) => `Laatst actief: ${when}`,
      takeover: 'Schrijfhulp overnemen',
    },
    takeoverDialog: {
      title: 'Schrijfhulp overnemen',
      description:
        'De huidige sessie stopt en een nieuwe sessie begint met het huidige concept als vertrekpunt. Doorgaan?',
      confirm: 'Overnemen',
      cancel: 'Annuleren',
    },
    restartDialog: {
      title: 'Nieuwe sessie starten',
      description:
        'De huidige sessie stopt en een nieuwe sessie begint met het huidige concept als vertrekpunt. Doorgaan?',
      confirm: 'Nieuwe sessie starten',
      cancel: 'Annuleren',
    },
  },
} as const;
